/**
 * Cohesive design tokens injected into HTML-block iframes.
 *
 * Blocks render in opaque-origin sandboxed iframes, so they cannot inherit
 * the app's CSS custom properties. The token values themselves come straight
 * from the real design-system stylesheets via `COHESIVE_IFRAME_THEME_CSS`
 * (see cohesiveThemeCss.ts) — single source of truth, no hand-ported copy.
 *
 * This module adds the only tokens that genuinely aren't in the stylesheets:
 * the block font stacks (the app loads its fonts through index.html, which an
 * iframe can't see) and the Google Fonts links that make them resolve.
 */

import { assembleBlockTokensCss } from "./blockTokensAssemble";
import { cohesiveThemeCssRaw, cohesiveTokensCssRaw } from "./cohesiveThemeCss";

/** Google Fonts stylesheet href shared by the block links below. */
export const COHESIVE_BLOCK_FONT_STYLESHEET_HREF =
  "https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&family=Source+Serif+4:ital,opsz,wght@0,8..60,200..900;1,8..60,200..900&display=swap";

/**
 * Google Fonts markup mirroring user-dashboard/index.html so block text can
 * use DM Sans / Source Serif 4. JetBrains Mono is self-hosted by the app and
 * not reachable cross-origin; --font-mono falls back to system monospace.
 *
 * The stylesheet is fetched as a `preload` that promotes itself to a
 * stylesheet once it lands, NOT as a plain `rel="stylesheet"`: a pending
 * stylesheet in `<head>` blocks every later parser-inserted script and the
 * document's `load` event, so a stalled fonts host (Safari Private Relay,
 * tracking prevention, per-host connection caps with dozens of frames) left
 * every block on the canvas blank indefinitely. With the preload the runtime
 * boots, `ready` is posted and the frame paints on the system stack; the
 * webfonts swap in when the stylesheet eventually arrives. `<noscript>` keeps
 * the plain link for documents rendered without scripting.
 */
export function createCohesiveBlockFontLinks(doc: Document): DocumentFragment {
  const fragment = doc.createDocumentFragment();
  const link = (attrs: Record<string, string>) => {
    const el = doc.createElement("link");
    for (const [name, value] of Object.entries(attrs)) {
      el.setAttribute(name, value);
    }
    el.setAttribute("data-cohesive-injected", "");
    return el;
  };
  fragment.appendChild(
    link({ rel: "preconnect", href: "https://fonts.googleapis.com" }),
  );
  fragment.appendChild(
    link({
      rel: "preconnect",
      href: "https://fonts.gstatic.com",
      crossorigin: "",
    }),
  );
  fragment.appendChild(
    link({
      rel: "preload",
      as: "style",
      href: COHESIVE_BLOCK_FONT_STYLESHEET_HREF,
      onload: "this.onload=null;this.rel='stylesheet'",
    }),
  );
  // Built as elements, not innerHTML: a parser with scripting enabled reads
  // <noscript> content as raw text and would serialize the link escaped.
  const noscript = doc.createElement("noscript");
  noscript.setAttribute("data-cohesive-injected", "");
  noscript.appendChild(
    link({ rel: "stylesheet", href: COHESIVE_BLOCK_FONT_STYLESHEET_HREF }),
  );
  fragment.appendChild(noscript);
  return fragment;
}

export interface CohesiveBlockTokensCssOptions {
  /** Prefix the webfont `@import` rules, for callers that inject this CSS as
   *  a lone stylesheet and therefore can't also add <link> elements. */
  fontImports?: boolean;
}

/**
 * The Cohesive token stylesheet for block iframes, ready to inject into a
 * block's <head>. Ships BOTH light and dark values — the stylesheet's own
 * `.dark` class selector picks the mode, and `prepareSrcDocHtml` sets that
 * class on the iframe's root when `colorScheme: "dark"`. Injected before the
 * document's own styles (and inside `@layer base`) so agent CSS that
 * deliberately redefines a var still wins.
 *
 * Assembly lives in blockTokensAssemble.ts, shared with the generator of the
 * hosted `block-tokens.v1.css` — the stylesheet the backend `<link>`s into
 * stored block HTML so previews/public shares render styled.
 */
export function getCohesiveBlockTokensCss(
  options: CohesiveBlockTokensCssOptions = {},
): string {
  return assembleBlockTokensCss(cohesiveTokensCssRaw, cohesiveThemeCssRaw, {
    fontImports: options.fontImports,
  });
}
